import AddIcon from '@mui/icons-material/Add'
import AlarmOffRoundedIcon from '@mui/icons-material/AlarmOffRounded'
import AlarmRoundedIcon from '@mui/icons-material/AlarmRounded'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import LocalStorageButtons from '@components/LocalStorageButtons'
import FilterGridContainer from '@components/toolbar/FilterGridContainer'
import FilterGridItem from '@components/toolbar/FilterGridItem'
import { FilterSelectGebied } from '@components/toolbar/FilterSelectGebied'
import { FilterSelectObject } from '@components/toolbar/FilterSelectObject'
import { FilterToggleButton } from '@components/toolbar/FilterToggleButton'
import type { GroupedFilterOption } from '@models/Filter'
import type { Gebied } from '@models/Gebied'

import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { useLocalFilters } from '../../core/hooks/useLocalFilters'
import { queryKey } from '../hooks/useProjectdossiersSearch'
import type { ProjectdossiersSearchFilters as Filters } from '../types/ProjectdossiersSearch'

export interface Props {
  onChange: (filters: Filters) => void
  filters: Filters
  initialFilters: Filters
  onRefresh: () => void
  onReset: () => void
  onCreate: () => void
  filterOptions: {
    gebieden: Gebied[]
    projectdossier: {
      status: { code: string; label: string }[]
      verantwoordelijken: GroupedFilterOption[]
    }
  }
}

export const ProjectdossiersFilters = ({
  onChange,
  filters,
  initialFilters,
  onRefresh,
  onReset,
  onCreate,
  filterOptions: {
    gebieden,
    projectdossier: { status: statussen, verantwoordelijken },
  },
}: Props) => {
  const { canCreateProjectdossier } = useAuthenticatedUser()
  const { localFilters, handleFilterChange, handleZoeken, handleReset } =
    useLocalFilters<Filters>({
      filters,
      initialFilters,
      onChange,
      onRefresh,
      onReset,
    })

  const { gebied, status, open, verantwoordelijke, projectdossierId } =
    localFilters

  return (
    <FilterGridContainer>
      <FilterGridItem>
        <FilterSelectGebied
          options={gebieden}
          value={gebied}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={statussen}
          label="Status"
          filterKey="status"
          value={status}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <FilterSelectObject
          options={verantwoordelijken}
          label="Verantwoordelijke"
          filterKey="verantwoordelijke"
          value={verantwoordelijke}
          onFilterChange={handleFilterChange}
        />
      </FilterGridItem>

      <FilterGridItem>
        <TextField
          label="Projectdossier id"
          variant="standard"
          fullWidth
          onChange={(event) =>
            handleFilterChange({ projectdossierId: event.currentTarget.value })
          }
          onKeyDown={(event) => {
            const ENTER_KEY = 13

            if (event.keyCode === ENTER_KEY) {
              handleZoeken()
            }
          }}
          value={projectdossierId || ''}
        />
      </FilterGridItem>

      <FilterGridItem
        minColumns="full"
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Tooltip title="Reset filters">
          <IconButton data-testid="clearbutton" onClick={handleReset}>
            <CloseIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Zoeken">
          <IconButton data-testid="filterbutton" onClick={handleZoeken}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        <FilterToggleButton
          filterKey="open"
          value={open}
          onFilterChange={handleFilterChange}
          activeIcon={<AlarmRoundedIcon />}
          inactiveIcon={<AlarmOffRoundedIcon />}
          activeTitle="Klik om ook afgesloten projectdossiers te tonen"
          inactiveTitle="Klik om alleen open projectdossiers te tonen"
        />

        {canCreateProjectdossier && (
          <Tooltip title="Nieuw projectdossier">
            <IconButton aria-label="Nieuw projectdossier" onClick={onCreate}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}

        <LocalStorageButtons
          name={queryKey}
          storable={localFilters}
          onClickLoad={handleFilterChange}
        />
      </FilterGridItem>
    </FilterGridContainer>
  )
}
